import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export default function LoginPage() {
  const { user, login, register, loading } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/visitor'} replace />;
  }

  const handleSignIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success('Signed in successfully');
      navigate(u.role === 'admin' ? '/admin' : '/visitor', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success('Account created');
      navigate('/visitor', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = () => {
    setEmail('admin@ticketrising.com');
    setPassword('Admin@123');
    setTab('signin');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      <div
        style={{
          flex: '0 0 42%',
          minWidth: 280,
          background: 'linear-gradient(160deg, #ff6b00 0%, #ff8c33 100%)',
          color: '#fff',
          padding: '2.5rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
        className="login-brand"
      >
        <h1 style={{ fontSize: '2rem', marginTop: 0 }}>Ticket Rising</h1>
        <p style={{ opacity: 0.95, lineHeight: 1.6 }}>
          QR-based visitor tickets, ratings, and analytics—built for front desks, facilities, and operations teams.
        </p>
        <ul style={{ paddingLeft: '1.2rem', lineHeight: 1.8 }}>
          <li>Instant QR links for every location</li>
          <li>Priority &amp; category routing</li>
          <li>Admin dashboard with trends</li>
        </ul>
        <Link to="/" style={{ color: '#fff', marginTop: '1rem', fontWeight: 600 }}>
          ← Back to home
        </Link>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', background: 'var(--bg-muted)' }}>
        <div className="tr-card" style={{ width: '100%', maxWidth: 420, padding: '2rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <button
              type="button"
              className="tr-btn"
              onClick={() => setTab('signin')}
              style={{
                flex: 1,
                background: tab === 'signin' ? 'var(--primary-soft)' : '#fff',
                color: tab === 'signin' ? 'var(--primary)' : 'var(--text-muted)',
                border: tab === 'signin' ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
              }}
            >
              Sign In
            </button>
            <button
              type="button"
              className="tr-btn"
              onClick={() => setTab('register')}
              style={{
                flex: 1,
                background: tab === 'register' ? 'var(--primary-soft)' : '#fff',
                color: tab === 'register' ? 'var(--primary)' : 'var(--text-muted)',
                border: tab === 'register' ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
              }}
            >
              Register
            </button>
          </div>

          {tab === 'signin' ? (
            <form onSubmit={handleSignIn}>
              <label className="tr-label" htmlFor="email">
                Email
              </label>
              <input id="email" className="tr-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />

              <label className="tr-label" htmlFor="password" style={{ marginTop: '1rem' }}>
                Password
              </label>
              <input
                id="password"
                className="tr-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />

              <button type="button" className="tr-btn tr-btn-ghost" style={{ marginTop: '0.75rem', padding: '0.35rem 0' }} onClick={fillDemo}>
                Use admin demo credentials
              </button>

              <button type="submit" className="tr-btn tr-btn-primary" style={{ width: '100%', marginTop: '1.25rem' }} disabled={busy}>
                {busy ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <label className="tr-label" htmlFor="name">
                Name
              </label>
              <input id="name" className="tr-input" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />

              <label className="tr-label" htmlFor="remail" style={{ marginTop: '1rem' }}>
                Email
              </label>
              <input id="remail" className="tr-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />

              <label className="tr-label" htmlFor="rpass" style={{ marginTop: '1rem' }}>
                Password
              </label>
              <input id="rpass" className="tr-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoComplete="new-password" />

              <button type="submit" className="tr-btn tr-btn-primary" style={{ width: '100%', marginTop: '1.25rem' }} disabled={busy}>
                {busy ? 'Creating…' : 'Create account'}
              </button>
            </form>
          )}

          <p style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            Demo admin: admin@ticketrising.com / Admin@123
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 800px) {
          .login-brand { display: none !important; }
        }
      `}</style>
    </div>
  );
}
